import { Card, FormInput } from "@general-backoffice/core/index";
import type { ReferrerHierarchyStats } from "@investhinks/api-client";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Badge, Card as RSCard, CardBody, FormGroup } from "reactstrap";

import formatCurrency from "../../utils/formatCurrency";
import MonthlyReferrerEarningsGraph from "./graphs/MonthlyReferrerEarningsGraph";

interface ReferrerOption {
  value: string | number | undefined;
  label: string;
  isOG: boolean;
}

interface MonthlyReferrersEarningsGraphCardProps {
  rawReferrersHierarchyStats?: ReferrerHierarchyStats[];
  isLoading: boolean;
}

const STARTING_YEAR = 2023;
const FINAL_YEAR = Number(moment().format("YYYY"));
const years: number[] = [];
for (let y = STARTING_YEAR; y <= FINAL_YEAR; y++) years.push(y);

const MonthlyReferrersEarningsGraphCard: React.FC<MonthlyReferrersEarningsGraphCardProps> = ({
  rawReferrersHierarchyStats = [],
  isLoading,
}) => {
  const hierarchy = true; // To swap between hierarchyTotal and total
  const currency = "EUR"; // TODO: in case other currencies are added
  const [year, setYear] = useState<string | number>(moment().format("YYYY"));

  const [referrers, setReferrers] = useState<ReferrerOption[] | null>(null);
  const referrersOptions = useMemo<ReferrerOption[]>(() => {
    if (!rawReferrersHierarchyStats) return [];
    return rawReferrersHierarchyStats.map(({ referrer: r, referredBy }) => ({
      value: r?.id,
      label: `${r?.name} (${r?.email})`,
      isOG: !referredBy,
    }));
  }, [rawReferrersHierarchyStats]);

  // Set initial referrers
  useEffect(() => {
    if (referrersOptions) setReferrers(referrersOptions.filter((r) => r?.isOG));
  }, [referrersOptions]);

  const [yearTotal, monthly] = useMemo<[number, Record<string, Record<string, number>>]>(() => {
    if (!rawReferrersHierarchyStats || !referrers) return [0, {}];
    const referrersIds = referrers.map((r) => r?.value);
    return rawReferrersHierarchyStats
      .filter((r) => referrersIds.includes(r?.referrer?.id))
      .reduce<[number, Record<string, Record<string, number>>]>(
        (r, referrer) => {
          const name = `${referrer?.referrer?.name} (${referrer?.referrer?.email})`;

          const charged = referrer?.charged;
          const yearCharges = charged?.[currency]?.perYear?.[year];
          if (!yearCharges) return r;
          r[0] += yearCharges?.[hierarchy ? "hierarchyTotal" : "total"] || 0;
          const perMonth = yearCharges?.perMonth;
          if (!perMonth) return r;
          r[1][name] = Object.entries(perMonth).reduce<Record<string, number>>(
            (r2, [month, totals]) => ({
              ...r2,
              [month]: totals?.[hierarchy ? "hierarchyTotal" : "total"] || 0,
            }),
            {}
          );
          return r;
        },
        [0, {}]
      );
  }, [rawReferrersHierarchyStats, referrers, year, hierarchy, currency]);

  return (
    <RSCard>
      <Card.Header title="Monthly earnings" subtitle={`Total: ${formatCurrency(yearTotal, currency)}`}>
        <div className="d-flex flex-wrap justify-content-end" style={{ gap: ".8rem 1rem" }}>
          <FormGroup className="mb--4" style={{ maxWidth: 350, minWidth: 200, flex: "1 1 200px" }}>
            <FormInput.Select
              placeholder="Select referrers"
              onChange={(v: ReferrerOption[] | null) => setReferrers(v || null)}
              value={referrers}
              options={referrersOptions}
              isClearable
              isMulti
              styles={{
                valueContainer: (base: Record<string, unknown>) => ({
                  ...base,
                  overflowX: "scroll",
                  flexWrap: "unset",
                }),
                multiValue: (base: Record<string, unknown>) => ({ ...base, flex: "1 0 auto" }),
              }}
            />
          </FormGroup>

          <FormGroup className="mb--4" style={{ minWidth: 120 }}>
            <FormInput.Select
              placeholder="Select a year"
              onChange={(opt: { value: string | number; label: string | number } | null) =>
                setYear(opt?.value || moment().format("YYYY"))
              }
              value={{ value: year, label: year }}
              options={years.map((y) => ({ value: y, label: y }))}
              isDisabled={isLoading}
              isClearable={false}
            />
          </FormGroup>
        </div>
      </Card.Header>
      <CardBody className="pb-4">
        <div className="position-relative w-100 mb-4">
          <div className="position-absolute d-flex top-0 left-0 w-100" style={{ transform: "translateY(-50%)" }}>
            <Badge color="default">{`Total: ${formatCurrency(yearTotal, currency)}`}</Badge>
          </div>
        </div>

        <MonthlyReferrerEarningsGraph
          data={monthly}
          currency={currency}
          isLoading={isLoading}
          className="rounded-bottom overflow-hidden"
          style={{ height: "calc(100vh - 400px)", minHeight: 400 }}
        />
      </CardBody>
    </RSCard>
  );
};

export default MonthlyReferrersEarningsGraphCard;
